import { createApi } from "@reduxjs/toolkit/query/react"
import { authQuery } from "@store/services/authQuery"
import { GraphNewDto, SampleRequestDto, SampleResponseDto } from "@features/builder/types"
import { GraphDto } from "@custom-types/graphs"

export type CheckExpressionRequestDto = {
  graphData: Blob
  nodeId: string
}

type CheckExpressionResponseDto = {
  message: string
}

export type GenerateLlmDescriptionRequestDto = {
  graph_id: string
  node_name: string
}

type GenerateLlmDescriptionResponseDto = {
  status: "queued"
  task_id: string
}

export const editorApi = createApi({
  reducerPath: "editorApi",
  baseQuery: authQuery,
  tagTypes: ["Document"],
  endpoints: (builder) => ({
    editorSample: builder.mutation<SampleResponseDto, SampleRequestDto>({
      query: (body) => {
        return { url: "/editor/sample", method: "POST", body }
      },
      invalidatesTags: ["Document"],
    }),
    editorNewGraph: builder.mutation<GraphDto, GraphNewDto>({
      query: (body) => ({
        url: "/editor/new-graph",
        method: "POST",
        body,
      }),
      invalidatesTags: ["Document"],
    }),
    checkExpression: builder.mutation<CheckExpressionResponseDto, CheckExpressionRequestDto>({
      query: ({ graphData, nodeId }) => {
        const formData = new FormData()
        formData.append("graph_data", graphData, "graph.tkrisk")
        return {
          url: `/tkrisk/expression-valid?node_ids=${nodeId}`,
          method: "POST",
          body: formData,
        }
      },
      invalidatesTags: [],
    }),
    generateLlmDescription: builder.mutation<
      GenerateLlmDescriptionResponseDto,
      GenerateLlmDescriptionRequestDto
    >({
      query: (body) => ({
        url: "/editor/llm-description/generate",
        method: "POST",
        body,
      }),
    }),
    editorExportGraph: builder.query<ArrayBuffer, string>({
      query: (key) => ({
        url: `/editor/export-graph?graph_id=${encodeURIComponent(key)}`,
        responseHandler: async (resp) => {
          if (resp.status !== 200) {
            return resp
          }
          return await resp.arrayBuffer()
        },
      }),
    }),
    editorImportGraph: builder.mutation<void, { graphKey: string; graphZip: File }>({
      query: ({ graphKey, graphZip }) => {
        const formData = new FormData()
        formData.append("graph_zip", graphZip, graphZip.name)

        return {
          url: `/editor/import-graph?graph_id=${encodeURIComponent(graphKey)}`,
          method: "POST",
          body: formData,
        }
      },
    }),
  }),
})

export const {
  useEditorSampleMutation,
  useEditorNewGraphMutation,
  useCheckExpressionMutation,
  useGenerateLlmDescriptionMutation,
  useLazyEditorExportGraphQuery,
  useEditorImportGraphMutation,
} = editorApi
